(() => {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.LumosFloatingPurchaseBar?.version) return;

  const ROOT_SELECTOR = '[id*="quick_add_to_cart_v2"]';
  const SMILE_SELECTOR = '#smile-ui-lite-launcher-frame-container';
  const KNOWN_HANDLES = new Set([
    'lumos-ultra',
    'lumos-nyxel'
  ]);

  const instances = new WeakMap();
  const liveInstances = new Set();

  function toFiniteNumber(value) {
    const number = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function normalizeMoneyFormat(format) {
    if (typeof format !== 'string' || format.trim() === '') return '{{amount}}';
    return format;
  }

  function formatAmount(cents, precision = 2, thousands = ',', decimal = '.') {
    const number = toFiniteNumber(cents);
    if (number === null) return '';

    const fixed = (number / 100).toFixed(precision).split('.');
    const integer = fixed[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return precision === 0 ? integer : `${integer}${decimal}${fixed[1]}`;
  }

  /**
   * Format Shopify's integer minor-unit amount without reading localized DOM
   * text. The storefront Currency helper remains the first choice when it is
   * available; the fallback covers the standard Shopify money placeholders.
   */
  function formatMoney(cents, format) {
    const amount = toFiniteNumber(cents);
    if (amount === null) return '';

    const currency = window.Currency;
    const themeFormat = format || window.theme?.moneyFormat || window.theme?.moneyWithCurrencyFormat;
    if (currency && typeof currency.formatMoney === 'function') {
      try {
        return currency.formatMoney(Math.round(amount), themeFormat);
      } catch (error) {
        // Fall through to the deterministic formatter when a legacy helper is
        // present but cannot handle the current store format.
      }
    }

    const template = normalizeMoneyFormat(themeFormat);
    const placeholder = template.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return template;

    const values = {
      amount: formatAmount(amount),
      amount_no_decimals: formatAmount(amount, 0),
      amount_with_comma_separator: formatAmount(amount, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatAmount(amount, 0, '.', ','),
      amount_with_apostrophe_separator: formatAmount(amount, 2, "'", '.'),
      amount_with_space_separator: formatAmount(amount, 2, ' ', ',')
    };
    const replacement = values[placeholder[1]];
    return replacement === undefined ? template : template.replace(placeholder[0], replacement);
  }

  function minorInteger(value) {
    if (typeof value === 'number') return Number.isInteger(value) ? value : null;
    if (typeof value !== 'string' || !/^-?\d+$/.test(value.trim())) return null;
    const number = Number(value);
    return Number.isSafeInteger(number) ? number : null;
  }

  function parseMajorAmount(value) {
    if (typeof value === 'number') return Math.round(value * 100);
    if (typeof value !== 'string' || value.trim() === '') return null;

    const normalized = value.trim().replace(/\s/g, '').replace(',', '.');
    if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return null;
    const number = Number(normalized);
    return Number.isFinite(number) ? Math.round(number * 100) : null;
  }

  function dataAttribute(node, names) {
    if (!node?.getAttribute) return null;
    for (const name of names) {
      const value = node.getAttribute(name);
      if (value !== null && value !== '') return value;
    }
    return null;
  }

  function numericData(node, key) {
    const nodes = [node, node?.closest?.('label'), node?.parentElement];
    const keys = key === 'compare'
      ? ['data-compare-price-cents', 'data-compare-price']
      : ['data-price-cents', 'data-price'];

    for (const current of nodes) {
      if (!current) continue;
      const value = dataAttribute(current, keys);
      if (value === null) continue;

      const unit = (dataAttribute(current, ['data-price-unit']) || '').toLowerCase();
      const isCentsKey = current.getAttribute(keys[0]) !== null;

      if (unit === 'major' || unit === 'decimal' || unit === 'dollars') {
        return parseMajorAmount(value);
      }

      // A plain data-price is a Shopify-style integer minor amount. Decimal
      // values without an explicit unit are intentionally rejected because
      // treating 19.95 as either 19.95 cents or 1995 cents is unsafe.
      if (isCentsKey || /^-?\d+$/.test(String(value).trim())) {
        return minorInteger(value);
      }
      return null;
    }
    return null;
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/[^a-zA-Z0-9_-]/g, (character) => `\\${character}`);
  }

  function imageSource(image) {
    if (!image) return '';
    return image.currentSrc || image.getAttribute?.('src') || image.getAttribute?.('data-src') || image.src || '';
  }

  function imageFromVariant(variant) {
    const candidates = [
      variant?.featured_image?.src,
      variant?.featured_image?.url,
      variant?.featured_media?.preview_image?.src,
      variant?.featured_media?.preview_image?.url,
      variant?.featured_media?.src,
      variant?.featured_media?.url,
      variant?.image?.src,
      variant?.image?.url
    ];
    return candidates.find((value) => typeof value === 'string' && value.trim() !== '') || '';
  }

  function text(value) {
    return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
  }

  function optionValues(variant) {
    if (!variant) return [];
    if (Array.isArray(variant.options)) return variant.options.filter(Boolean).map(String);
    return [variant.option1, variant.option2, variant.option3].filter(Boolean).map(String);
  }

  function isVisible(node) {
    if (!node) return false;
    if (node.hidden || node.getAttribute?.('aria-hidden') === 'true') return false;
    return !node.closest?.('[hidden], [aria-hidden="true"]');
  }

  function firstImage(root, selectors) {
    if (!root?.querySelectorAll) return '';
    for (const selector of selectors) {
      let matches = [];
      try {
        matches = Array.from(root.querySelectorAll(selector));
      } catch (error) {
        continue;
      }
      const image = matches.find((candidate) => isVisible(candidate) && imageSource(candidate));
      if (image) return imageSource(image);
    }
    return '';
  }

  function productHandle(root, form) {
    const input = root?.querySelector?.('.global-product-handle') || form?.querySelector?.('.global-product-handle');
    return text(input?.value || root?.dataset?.productHandle || form?.dataset?.productHandle).toLowerCase();
  }

  function parseProductJson(form) {
    const script = form?.querySelector?.('[data-product-json]');
    if (!script) return null;
    try {
      const parsed = JSON.parse(script.textContent || '{}');
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed.product && typeof parsed.product === 'object' ? parsed.product : parsed;
    } catch (error) {
      return null;
    }
  }

  function findVariant(product, id) {
    if (!product?.variants || id === null || id === undefined || id === '') return null;
    return product.variants.find((variant) => String(variant.id) === String(id)) || null;
  }

  function selectedVariantId(form) {
    const input = form?.querySelector?.('input[name="id"], select[name="id"]');
    return input?.value || input?.selectedOptions?.[0]?.value || null;
  }

  function buttonInsideMainForm(form, selector) {
    const buttons = Array.from(form?.querySelectorAll?.(selector) || []);
    return buttons.find((button) => !button.closest?.('.firefly-popup')) || null;
  }

  function productRootFor(form) {
    return form?.closest?.('[data-section-type="product"]:not(.popup-bundle)') ||
      form?.closest?.('.Product') ||
      form?.parentElement || null;
  }

  function payloadAmount(payload, names) {
    if (!payload || typeof payload !== 'object') return null;
    const value = names.map((name) => payload[name]).find((candidate) => candidate !== undefined && candidate !== null);
    if (value === undefined) return null;
    const unit = String(payload.price_unit || payload.priceUnit || '').toLowerCase();
    if (unit === 'major' || unit === 'decimal' || unit === 'dollars') return parseMajorAmount(value);
    return minorInteger(value);
  }

  function eventSourceMatches(event, form, root) {
    if (!event) return false;
    const detailForm = event.detail?.form;
    if (detailForm && detailForm !== form) return false;

    const target = event.target;
    if (!target || target === document || target === document.documentElement || target === document.body) {
      return Boolean(detailForm === form);
    }
    return target === form || form?.contains?.(target) || root?.contains?.(target);
  }

  function stateVariant(product, detail, selectedId) {
    const selected = findVariant(product, selectedId);
    if (selected) return selected;

    const eventVariant = detail?.variant;
    if (eventVariant?.id !== undefined && eventVariant?.id !== null) {
      return findVariant(product, eventVariant.id) || eventVariant;
    }
    if (detail?.variantId !== undefined && detail.variantId !== null) {
      return findVariant(product, detail.variantId);
    }
    return null;
  }

  function uniqueNodes(nodes) {
    return Array.from(new Set(nodes.filter(Boolean)));
  }

  class FloatingPurchaseBar {
    constructor(section) {
      this.section = section;
      this.form = null;
      this.productRoot = null;
      this.mainButton = null;
      this.product = null;
      this.handle = '';
      this.knownHandle = false;
      this.gallery = null;
      this.domObserver = null;
      this.intersectionObserver = null;
      this.resizeObserver = null;
      this.smileObserver = null;
      this.formChangeListener = null;
      this.stateListener = this.handleState.bind(this);
      this.addedListener = this.handleAdded.bind(this);
      this.addErrorListener = this.handleAddError.bind(this);
      this.nyxelStateListener = this.handleNyxelState.bind(this);
      this.addToCartListeners = [];
      this.visibilityScrollListener = this.updateVisibility.bind(this);
      this.visibilityResizeListener = this.handleResize.bind(this);
      this.raf = null;
      this.smileBottom = '16px';
      this.lastState = null;
      this.nyxelState = null;
      this.buyNowPending = false;
      this.buyNowSourceForm = null;
      this.buyNowVariantId = null;
      this.destroyed = false;
      this.isVisible = false;
      this.buyNowLabel = text(this.section.querySelector?.('.ProductForm__BuyNow')?.textContent) ||
        text(window.languages?.productFormBuyNow) || 'Buy now';
      this.addToCartFallbackLabel = text(window.languages?.productFormAddToCart) || 'Add to cart';
      this.boundAddToCart = this.forwardAddToCart.bind(this);
      this.boundBuyNow = this.startBuyNow.bind(this);
      this.section.style.display = 'none';
      this.section.setAttribute('aria-hidden', 'true');
    }

    init() {
      this.refreshContext();
      if (!this.knownHandle) return;
      document.addEventListener('product:state', this.stateListener);
      document.addEventListener('product:added', this.addedListener);
      document.addEventListener('product:add-error', this.addErrorListener);
      document.addEventListener('nyxel:purchase-state-changed', this.nyxelStateListener);
      this.bindFloatingButtons();
      this.updateVisibility();
    }

    bindFloatingButtons() {
      const buttons = uniqueNodes([
        ...Array.from(this.section.querySelectorAll?.('.ProductForm__AddToCart') || []),
        ...Array.from(this.section.querySelectorAll?.('.ProductForm__BuyNow') || [])
      ]);
      buttons.forEach((button) => {
        if (button.classList.contains('ProductForm__BuyNow')) {
          button.addEventListener('click', this.boundBuyNow);
          this.addToCartListeners.push({ button, type: 'click', listener: this.boundBuyNow });
        } else {
          button.addEventListener('click', this.boundAddToCart);
          this.addToCartListeners.push({ button, type: 'click', listener: this.boundAddToCart });
        }
      });
    }

    refreshContext() {
      if (this.destroyed) return;
      const nextForm = document.querySelector(
        '[data-section-type="product"]:not(.popup-bundle) .ProductForm'
      );
      const nextRoot = productRootFor(nextForm);
      const nextButton = buttonInsideMainForm(nextForm, '.ProductForm__BuyButtons .ProductForm__AddToCart');
      if (nextForm === this.form && nextButton === this.mainButton) {
        this.sync();
        return;
      }

      this.unbindContext();
      this.form = nextForm;
      this.productRoot = nextRoot;
      this.mainButton = nextButton;
      this.product = parseProductJson(nextForm);
      this.handle = productHandle(nextRoot, nextForm);
      this.knownHandle = KNOWN_HANDLES.has(this.handle);
      this.gallery = nextRoot?.querySelector?.(
        '[data-ultra-mobile-gallery], [data-nyxel-gallery], .product-img-container, .product-showcase'
      ) || null;

      if (!this.knownHandle || !this.form || !this.mainButton) {
        this.setVisibility(false);
        return;
      }

      this.formChangeListener = () => this.scheduleSync();
      this.form.addEventListener('change', this.formChangeListener);

      if (typeof MutationObserver === 'function') {
        this.domObserver = new MutationObserver(() => this.scheduleSync());
        this.domObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'class', 'aria-label', 'aria-busy', 'data-action'],
          childList: true,
          subtree: true
        });
        if (this.gallery) {
          this.domObserver.observe(this.gallery, {
            attributes: true,
            attributeFilter: ['class', 'hidden', 'aria-hidden', 'src', 'data-src'],
            childList: true,
            subtree: true
          });
        }
      }

      this.setupVisibilityObserver();
      this.sync();
    }

    unbindContext() {
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
      this.domObserver?.disconnect();
      this.domObserver = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.form?.removeEventListener('change', this.formChangeListener);
      this.formChangeListener = null;
      window.removeEventListener('scroll', this.visibilityScrollListener);
      window.removeEventListener('resize', this.visibilityResizeListener);
      this.mainButton = null;
      this.form = null;
      this.productRoot = null;
      this.gallery = null;
    }

    setupVisibilityObserver() {
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
      window.removeEventListener('scroll', this.visibilityScrollListener);
      window.removeEventListener('resize', this.visibilityResizeListener);

      if (!this.mainButton) return;

      if (typeof IntersectionObserver === 'function') {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          this.setVisibility(!(entry?.isIntersecting || entry?.intersectionRatio > 0));
        }, { threshold: 0 });
        this.intersectionObserver.observe(this.mainButton);
        window.addEventListener('resize', this.visibilityResizeListener);
      } else {
        window.addEventListener('scroll', this.visibilityScrollListener, { passive: true });
        window.addEventListener('resize', this.visibilityResizeListener);
      }

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.handleResize());
        this.resizeObserver.observe(this.section);
      }
    }

    handleResize() {
      this.updateVisibility();
      if (this.isVisible) this.updateSmilePosition(true);
    }

    updateVisibility() {
      if (!this.mainButton?.getBoundingClientRect) {
        this.setVisibility(false);
        return;
      }
      const rect = this.mainButton.getBoundingClientRect();
      const outside = rect.bottom < 0 || rect.top > window.innerHeight;
      this.setVisibility(outside);
    }

    setVisibility(shouldShow) {
      const next = Boolean(shouldShow && this.mainButton);
      if (next === this.isVisible && this.section.getAttribute('aria-hidden') === (next ? 'false' : 'true')) {
        if (next) this.updateSmilePosition(true);
        return;
      }
      this.isVisible = next;
      this.section.style.display = next ? '' : 'none';
      this.section.setAttribute('aria-hidden', next ? 'false' : 'true');
      this.updateSmilePosition(next);
    }

    updateSmilePosition(watchForLauncher) {
      this.disconnectSmileObserver();
      const height = this.isVisible
        ? (this.section.getBoundingClientRect?.().height || this.section.offsetHeight || 0)
        : 0;
      this.smileBottom = this.isVisible ? `${height + 5}px` : '16px';

      const apply = () => {
        const launcher = document.querySelector(SMILE_SELECTOR);
        if (!launcher) return false;
        launcher.style.bottom = this.smileBottom;
        return true;
      };

      if (apply() || !watchForLauncher || typeof MutationObserver !== 'function' || !document.body) return;
      this.smileObserver = new MutationObserver(() => {
        if (apply()) this.disconnectSmileObserver();
      });
      this.smileObserver.observe(document.body, { childList: true, subtree: true });
    }

    disconnectSmileObserver() {
      this.smileObserver?.disconnect();
      this.smileObserver = null;
    }

    scheduleSync() {
      if (this.destroyed || this.raf !== null) return;
      const callback = () => {
        this.raf = null;
        this.sync();
        this.updateVisibility();
      };
      if (typeof window.requestAnimationFrame === 'function') {
        this.raf = window.requestAnimationFrame(callback);
      } else {
        callback();
      }
    }

    deriveState() {
      const selectedId = selectedVariantId(this.form);
      const detail = this.lastState || {};
      const nyxel = this.nyxelState || {};
      const status = detail.status || nyxel.status;
      const eventDetail = detail.variant || detail.variantId ? detail : nyxel;
      let variant = stateVariant(this.product, eventDetail, selectedId);

      if (variant && nyxel.variantId && String(variant.id) === String(nyxel.variantId)) {
        variant = {
          ...variant,
          price: nyxel.price ?? variant.price,
          compare_at_price: nyxel.compareAtPrice ?? variant.compare_at_price
        };
      }

      if (status === 'incomplete' || status === 'unavailable') variant = null;
      if (status === 'sold-out' && variant) variant = { ...variant, available: false };

      return {
        detail,
        variant,
        pending: Boolean(
          detail.pending || nyxel.pending ||
          this.mainButton?.getAttribute?.('aria-busy') === 'true'
        ),
        selectedId,
        status,
        sourceForm: this.form
      };
    }

    selectedAddonInputs() {
      if (!this.form) return [];
      return Array.from(this.form.querySelectorAll(
        '.SizeSwatch__Radio.addon:checked, .ProductForm__Option.addon-extra.v2 input[name="option-addon"]:checked'
      )).filter((input) => !input.closest?.('.firefly-popup'));
    }

    addonTotals(state) {
      let total = 0;
      let compareAt = 0;
      let complete = true;
      const requestAddons = state.detail?.addons || state.detail?.request?.addons;
      const requestById = new Map(
        Array.isArray(requestAddons)
          ? requestAddons.map((addon) => [String(addon.id ?? addon.variantId), addon])
          : []
      );

      this.selectedAddonInputs().forEach((input) => {
        const id = input.getAttribute('data-id') || input.getAttribute('data-addon-variant-id');
        const requestAddon = id ? requestById.get(String(id)) : null;
        const price = requestAddon
          ? payloadAmount(requestAddon, ['price_cents', 'price_minor', 'price'])
          : numericData(input, 'price');
        const addonCompare = requestAddon
          ? payloadAmount(requestAddon, ['compare_at_price_cents', 'compareAtPrice', 'compare_at_price', 'compare_at_price_minor'])
          : numericData(input, 'compare');

        // Do not turn localized label text into a number. A checked add-on
        // without numeric data is incomplete for this summary; the native
        // product form remains the purchase authority.
        if (price === null) {
          complete = false;
          return;
        }
        total += price;
        compareAt += addonCompare === null ? price : addonCompare;
      });

      return { total, compareAt, complete };
    }

    variantImage(variant) {
      const id = variant?.id;
      const sku = text(variant?.sku);
      const exactSelectors = [];

      if (id !== undefined && id !== null) {
        const escapedId = cssEscape(id);
        exactSelectors.push(
          `[data-ebike-gallery-variant-id="${escapedId}"] img`,
          `[data-ebike-gallery-key="${escapedId}"] img`,
          `[data-variant-id="${escapedId}"] img`,
          `[data-product-variant-id="${escapedId}"] img`
        );
      }
      if (sku) {
        const escapedSku = cssEscape(sku);
        exactSelectors.push(
          `[data-sku="${escapedSku}"] img`,
          `[data-variant-sku="${escapedSku}"] img`
        );
      }

      const fromActiveGallery = firstImage(this.productRoot, exactSelectors);
      if (fromActiveGallery) return fromActiveGallery;

      const fromVariant = imageFromVariant(variant);
      if (fromVariant) return fromVariant;

      return '';
    }

    renderProductImage(variant) {
      const container = this.section.querySelector('.prod-image');
      if (!container) return;
      const source = this.variantImage(variant);
      if (!source) {
        container.replaceChildren();
        return;
      }
      const current = container.querySelector('img');
      if (current && imageSource(current) === source) return;

      const image = document.createElement('img');
      image.src = source;
      image.alt = text(variant?.title || variant?.sku || this.product?.title);
      container.replaceChildren(image);
    }

    renderAddonImages() {
      const container = this.section.querySelector('.addon-image');
      if (!container) return;
      const images = [];
      this.selectedAddonInputs().forEach((input) => {
        const source = input.closest?.('label')?.querySelector?.('.bundle-product-info img');
        if (!source) return;
        const image = source.cloneNode?.(true) || document.createElement('img');
        if (!image.src) image.src = imageSource(source);
        images.push(image);
      });
      container.replaceChildren(...images);
    }

    renderMips(variant) {
      const mips = this.section.querySelector('.image-wrapper .mips');
      if (!mips) return;
      const values = optionValues(variant);
      const checked = this.form?.querySelector?.(
        'input[data-variant="Helmet Type"]:checked, input[data-variant="Modell"]:checked, input[data-variant="Model (Size: M-L)"]:checked, input[data-variant="Modell (Größe: M-L)"]:checked'
      );
      const source = values.concat(checked?.value || []).join(' ');
      const show = /\bmips\b/i.test(source) && !/non[-\s]?mips/i.test(source);
      mips.classList.toggle('hidden', !show);
    }

    labelFromMainButton() {
      const explicit = text(
        this.mainButton?.getAttribute?.('data-label') ||
        this.mainButton?.getAttribute?.('data-add-to-cart-label') ||
        this.mainButton?.getAttribute?.('aria-label')
      );
      const labelNode = this.mainButton?.querySelector?.(
        'span:not(.Button__SeparatorDot):not(.total-price), [data-add-to-cart-label]'
      );
      return explicit || text(labelNode?.textContent) ||
        text(window.languages?.productFormAddToCart) ||
        this.addToCartFallbackLabel || 'Add to cart';
    }

    unavailableLabel() {
      const current = text(this.mainButton?.textContent);
      if (current) return current;
      return text(window.languages?.productFormUnavailable) ||
        text(window.languages?.productFormSoldOut) || 'Unavailable';
    }

    renderButtons(state, totals) {
      const variantAvailable = Boolean(state.variant?.available !== false && state.variant);
      const mainDisabled = Boolean(this.mainButton?.disabled || this.mainButton?.hasAttribute?.('disabled'));
      const incomplete = state.status === 'incomplete' || !state.variant;
      const available = variantAvailable && !incomplete && !state.pending && !mainDisabled && !this.buyNowPending;
      const unavailable = !available;
      const addLabel = this.labelFromMainButton();
      const unavailableText = state.pending ? text(this.mainButton?.textContent) || 'Adding…' : this.unavailableLabel();
      const addButtons = Array.from(this.section.querySelectorAll('.ProductForm__AddToCart'));

      addButtons.forEach((button) => {
        button.disabled = unavailable;
        button.setAttribute('aria-disabled', String(unavailable));
        button.setAttribute('aria-busy', String(Boolean(state.pending || this.buyNowPending)));
        button.classList.toggle('Button--primary', available);
        button.classList.toggle('Button--secondary', unavailable);
        button.replaceChildren();

        const isMobile = button.classList.contains('mobile');
        const label = document.createElement('span');
        label.textContent = unavailable ? unavailableText : addLabel;
        button.appendChild(label);

        if (available && !isMobile && totals.total !== null) {
          const separator = document.createElement('span');
          separator.className = 'Button__SeparatorDot';
          separator.textContent = ' | ';
          const price = document.createElement('span');
          price.className = 'total-price';
          price.innerHTML = formatMoney(totals.total);
          button.append(separator, price);
        }
      });

      const buyNow = this.section.querySelector('.ProductForm__BuyNow');
      if (!buyNow) return;
      buyNow.disabled = unavailable;
      buyNow.setAttribute('aria-disabled', String(unavailable));
      buyNow.setAttribute('aria-busy', String(Boolean(state.pending || this.buyNowPending)));
      buyNow.classList.toggle('Button--primary', available);
      buyNow.classList.toggle('Button--disabled', unavailable);
      if (!buyNow.textContent.trim()) {
        const label = document.createElement('div');
        label.textContent = this.buyNowLabel;
        buyNow.appendChild(label);
      }
    }

    renderPrices(totals) {
      const priceWrapper = this.section.querySelector('.price-wrapper');
      if (!priceWrapper) return;
      const highlight = priceWrapper.querySelector('.Price--highlight');
      const compare = priceWrapper.querySelector('.Price--compareAt');
      const save = priceWrapper.querySelector('.price-save');
      if (totals.total === null) {
        if (highlight) highlight.innerHTML = '';
        if (compare) compare.hidden = true;
        if (save) save.hidden = true;
        return;
      }
      if (highlight) highlight.innerHTML = formatMoney(totals.total);

      const compareAmount = totals.compareAt > totals.total ? totals.compareAt : null;
      if (compare && save && compareAmount !== null) {
        compare.hidden = false;
        save.hidden = false;
        compare.innerHTML = formatMoney(compareAmount);
        const label = text(window.languages?.productFormSave) || 'Save';
        save.replaceChildren();
        const saveLabel = document.createElement('span');
        saveLabel.textContent = `${label} `;
        const saveAmount = document.createElement('span');
        saveAmount.innerHTML = formatMoney(compareAmount - totals.total);
        save.append(saveLabel, saveAmount);
      } else {
        if (compare) compare.hidden = true;
        if (save) save.hidden = true;
      }
    }

    sync() {
      if (this.destroyed || !this.form || !this.mainButton) return;
      if (!this.product) this.product = parseProductJson(this.form);
      const state = this.deriveState();
      const addon = this.addonTotals(state);
      const variantPrice = minorInteger(state.variant?.price);
      const variantCompare = minorInteger(state.variant?.compare_at_price);
      const totalIsKnown = addon.complete;
      const totals = {
        total: variantPrice === null || !totalIsKnown ? null : variantPrice + addon.total,
        compareAt: variantPrice === null || !totalIsKnown
          ? 0
          : Math.max(variantCompare === null ? variantPrice : variantCompare, variantPrice) + addon.compareAt,
        addonDataComplete: totalIsKnown
      };

      this.renderProductImage(state.variant);
      this.renderAddonImages();
      this.renderMips(state.variant);
      this.renderPrices(totals);
      this.renderButtons(state, totals);
    }

    handleState(event) {
      if (!eventSourceMatches(event, this.form, this.productRoot)) return;
      this.lastState = event.detail || {};
      this.scheduleSync();
    }

    handleNyxelState(event) {
      if (!eventSourceMatches(event, this.form, this.productRoot)) return;
      this.nyxelState = event.detail || {};
      this.scheduleSync();
    }

    handleAdded(event) {
      if (!this.buyNowPending || !eventSourceMatches(event, this.buyNowSourceForm, this.productRoot)) return;
      const variantId = event.detail?.variant?.id ?? event.detail?.variant;
      if (this.buyNowVariantId !== null && variantId !== undefined &&
          String(variantId) !== String(this.buyNowVariantId)) return;
      this.finishBuyNow(true);
    }

    handleAddError(event) {
      if (!this.buyNowPending || !eventSourceMatches(event, this.buyNowSourceForm, this.productRoot)) return;
      this.finishBuyNow(false);
    }

    forwardAddToCart(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (this.destroyed || !this.mainButton || this.mainButton.disabled || this.buyNowPending) return;
      this.mainButton.click();
    }

    startBuyNow(event) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (this.destroyed || !this.mainButton || this.mainButton.disabled || this.buyNowPending) return;

      this.buyNowPending = true;
      this.buyNowSourceForm = this.form;
      this.buyNowVariantId = this.deriveState().variant?.id ?? selectedVariantId(this.form);
      window.closeDrawer = true;
      this.sync();
      this.mainButton.click();
    }

    finishBuyNow(success) {
      this.buyNowPending = false;
      this.buyNowSourceForm = null;
      this.buyNowVariantId = null;
      if (!success) {
        window.closeDrawer = false;
        this.sync();
        return;
      }
      window.location.assign('/checkout');
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      document.removeEventListener('product:state', this.stateListener);
      document.removeEventListener('product:added', this.addedListener);
      document.removeEventListener('product:add-error', this.addErrorListener);
      document.removeEventListener('nyxel:purchase-state-changed', this.nyxelStateListener);
      this.addToCartListeners.forEach(({ button, type, listener }) => button.removeEventListener(type, listener));
      this.addToCartListeners = [];
      this.unbindContext();
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.disconnectSmileObserver();
      if (this.raf !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(this.raf);
      }
      this.raf = null;
      const launcher = document.querySelector(SMILE_SELECTOR);
      if (launcher) launcher.style.bottom = '16px';
      liveInstances.delete(this);
    }
  }

  function init(section) {
    if (!section || instances.has(section)) return instances.get(section);
    const instance = new FloatingPurchaseBar(section);
    instances.set(section, instance);
    liveInstances.add(instance);
    instance.init();
    return instance;
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  function handleShopifySection(event) {
    const target = event.target;
    if (!target) return;
    if (event.type === 'shopify:section:unload') {
      const instance = instances.get(target);
      instance?.destroy();
      return;
    }
    if (target.matches?.(ROOT_SELECTOR)) init(target);
    liveInstances.forEach((instance) => {
      if (instance.section.isConnected === false) instance.destroy();
      else if (instance.productRoot?.contains?.(target) || instance.section.contains?.(target)) instance.refreshContext();
    });
  }

  document.addEventListener('shopify:section:load', handleShopifySection);
  document.addEventListener('shopify:section:unload', handleShopifySection);

  window.LumosFloatingPurchaseBar = {
    version: '1.0.0',
    knownHandles: [...KNOWN_HANDLES],
    formatMoney,
    parseMajorAmount,
    numericData,
    init,
    initAll,
    destroy(section) {
      instances.get(section)?.destroy();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
